/**
 * /customer/settings/account — Account sub-page (W-Apple).
 *
 * Houses the destructive "Delete Account" CTA per Apple Guideline
 * 5.1.1(v). Profile→Settings→Account = 3-tap discoverability target.
 */
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, ShieldAlert, ChevronRight, Trash2 } from 'lucide-react';
import { useAuthStore } from '@/lib/auth';

export default function AccountSettingsPage() {
  const user = useAuthStore((s) => s.user);

  return (
    <div className="space-y-6" data-testid="customer-account-settings-screen">
      <header className="flex items-center gap-3">
        <Link
          to="/customer/settings"
          className="p-2 -ml-2 rounded-full hover:bg-white/5 text-white/70"
          aria-label="Back to settings"
          data-testid="account-settings-back"
        >
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
          Account
        </h1>
      </header>

      {/* Identity card */}
      <section className="rounded-2xl border border-white/8 bg-[#15151B] p-5">
        <div className="flex items-center gap-3 text-white/80">
          <Mail size={18} className="text-[#D4AF37]" />
          <span className="text-sm">{user?.email}</span>
        </div>
        {user?.membership_id ? (
          <div className="mt-3 text-xs text-white/50">
            Member ID: <span className="font-mono">{user.membership_id}</span>
          </div>
        ) : null}
      </section>

      {/* Danger zone */}
      <section className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-5">
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} className="text-red-400 mt-0.5" />
          <div className="flex-1">
            <h2 className="text-white font-semibold">Danger zone</h2>
            <p className="mt-1 text-sm text-white/60">
              Permanently delete your OneStamp account. This action is
              irreversible after the 30-day grace period.
            </p>
          </div>
        </div>
        <Link
          to="/customer/settings/account/delete"
          data-testid="open-delete-account"
          className="mt-4 flex items-center justify-between rounded-xl border border-red-500/30 bg-transparent px-4 py-3 text-red-400 hover:bg-red-500/[0.08] transition"
        >
          <span className="flex items-center gap-2">
            <Trash2 size={16} />
            <span className="font-medium">Delete Account</span>
          </span>
          <ChevronRight size={18} />
        </Link>
      </section>
    </div>
  );
}
